import {Component, inject} from '@angular/core';
import {AggregationLevelPipe} from 'src/app/shared/pipes/aggregation-level.pipe';
import {FormControl, FormGroup, ReactiveFormsModule} from '@angular/forms';
import {LegendMetric} from 'src/app/shared/enums/legend-metric.enum';
import {GenOutageAggregationLevel} from 'generated/serverModels/GenOutageAggregationLevel';
import {LegendNamePipe} from '../../pipes/legend-name.pipe';
import {UserService} from '../../services/user.service';
import {toSignal} from '@angular/core/rxjs-interop';
import {LegendMetricPipe} from 'src/app/shared/pipes/legend-metric.pipe';
import {tap} from 'rxjs';
import {AggregationLevel, LocationData, VulcanLocationFilterComponent} from '@eagle-i/vulcan-component-library';
import {DataService} from 'src/app/shared/services/data.service';
import {LegendStyle} from '../../classes/legend-style';
import {MatFormField, MatLabel} from '@angular/material/form-field';
import {MatOption, MatSelect} from '@angular/material/select';
import {NomLegendComponent} from 'src/app/shared/components/nom-legend/nom-legend.component';
import {MatButton} from '@angular/material/button';
import {GenStatePreference} from 'generated/serverModels/GenStatePreference';
import {UserPreference} from 'src/app/shared/classes/user-preferences';
import {MatSnackBar} from '@angular/material/snack-bar';
import {AuthenticationService} from 'src/app/shared/services/authentication.service';
import {ApplicationConfig} from 'src/app/shared/classes/application-config';
import {Router} from '@angular/router';
import {GlobalConfigService} from 'src/app/shared/services/global-config.service';
import {GlobalTimesliderService} from 'src/app/shared/components/global-timeslider/global-timeslider.service';

@Component({
  selector: 'eaglei-user-preference',
  imports: [
    AggregationLevelPipe,
    ReactiveFormsModule,
    LegendNamePipe,
    LegendMetricPipe,
    VulcanLocationFilterComponent,
    MatFormField,
    MatSelect,
    MatOption,
    NomLegendComponent,
    MatLabel,
    MatButton,
  ],
  templateUrl: './user-preference.component.html',
  styleUrl: './user-preference.component.scss',
  standalone: true
})
export class UserPreferenceComponent {
    private userService = inject(UserService);
    private authService = inject(AuthenticationService);
    private timesliderService = inject(GlobalTimesliderService);
    private popup = inject(MatSnackBar);
    private router = inject(Router);
    private globalConfigService = inject(GlobalConfigService);

    private user = toSignal(this.authService.authenticatedUser);

    protected preferencesGroup = new FormGroup({
        aggregationLevel: new FormControl<GenOutageAggregationLevel>(GenOutageAggregationLevel.state, {
            nonNullable: true,
        }),
        states: new FormControl<string[]>(null, {nonNullable: true}),
        legendMetric: new FormControl<number>(LegendMetric.CUSTOMERS_OUT, {nonNullable: true}),
        legendStyle: new FormControl<LegendStyle>(undefined),
    });

    protected readonly aggregationLevels = [GenOutageAggregationLevel.state, GenOutageAggregationLevel.county];
    protected legendStyles = toSignal(
        this.userService.getLegendOptions().pipe(
            tap((styles) => {
                this.preferencesGroup.controls.legendStyle.setValue(styles[0]);
            }),
        ),
    );

    protected legendMetrics = LegendMetric.values();

    protected defaultStateCodes = DataService.states.getValue().map((state) => {
        return state.geoid;
    });

    protected styleCompare(o1: LegendStyle, o2: LegendStyle): boolean {
        return o1.id === o2.id;
    }

    protected updateSelectedLocation(event: LocationData): void {
        this.preferencesGroup.patchValue({
            states: event.stateCodes,
        });
    }

    protected savePreferences() {
        const preferences = new UserPreference();
        preferences.userId = this.user()?.id;
        preferences.setAggregationLevel(this.preferencesGroup.controls.aggregationLevel.value);

        preferences.legendStyle = this.preferencesGroup.controls.legendStyle.value;
        preferences.legendMetric = this.preferencesGroup.controls.legendMetric.value as number;

        const selectedStates = DataService.states
            .getValue()
            .filter((state) => this.preferencesGroup.controls.states.value.includes(state.geoid));
        preferences.states = selectedStates.map((state) => {
            const preferenceState = new GenStatePreference();
            preferenceState.id = state.id;
            preferenceState.name = state.name;
            preferenceState.abbreviation = state.abbreviation;
            preferenceState.stateAbbreviation = state.abbreviation;
            return preferenceState;
        });

        this.userService.savePreferences(preferences).subscribe({
            next: () => {
                this.popup.open('Preferences updated.', 'X', {duration: 5000, panelClass: 'dialog-success'});
                ApplicationConfig.currentUserPreferences.next(preferences);

                this.globalConfigService.selectedLegendMetric.update(() => {
                    return LegendMetric.from(preferences.legendMetric);
                });

                const stateCodes = selectedStates.map((state) => state.geoid);
                this.timesliderService.setLocationAndAggregationSelection({
                    aggregationLevel: preferences.getOutageAggregationLevel().toString() as AggregationLevel,
                    stateCodes,
                    stateAbbreviations: selectedStates.map((state) => state.abbreviation),
                    countyCodes: DataService.getCountiesFromStateIds(stateCodes).map((county) => county.geoid),
                });

                this.router.navigateByUrl('/app/landing');
            },
            error: (e) => {
                const text = e.error.userMessage || 'Failed to update preferences.';
                this.popup.open(text, 'X', {duration: 5000, panelClass: 'dialog-failure'});
            },
        });
    }
}
